/* eslint-disable max-lines */
import { useCombobox, UseComboboxStateChange } from "downshift";
import { isEqual } from "lodash";
import { cx } from "../../../utils/classnames";
import useSelect from "./hook/useSelect";
import { ISelectAsync, ISelectOptions } from "./Select.types";
import List from "./shared/List";
import SelectInput from "./shared/SelectInput";
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const Select = <T, D extends unknown>({
  asyncFunc,
  getOptionLabel,
  itemToString,
  selectedItem: _selectedItem,
  selectedItems,
  inputClassName,
  emptyItem,
  onSelectedItemChange,
  className,
  pageSize,
  options,
  searchQuerySelector,
  searchFunc,
  selectType = "single",
  onSelectedListChange,
  dataSelector,
  extraParams,
  maxHeightList,
  isAbsolute,
  getOptionValue: _getOptionValue,
  defaultIsOpen,
  enableKeyboardNav,
  onEnterSelect,
  ...props
}: ISelectAsync<T, D> | ISelectOptions<T, D>) => {
  // Only route keys to the in-menu search input for multi-select opt-ins.
  // Single-select already gets full nav from the outer input; leaving it off
  // there avoids fighting Downshift's single input ref.
  const keyboardNav = !!enableKeyboardNav && selectType === "multiple";
  // Opt-in: when a consumer wants Enter to advance focus, Enter closes the menu
  // and fires onEnterSelect. Gated to the keyboard-nav multi-select so no other
  // consumer's keep-open-on-Enter behavior changes.
  const closeOnEnter = keyboardNav && !!onEnterSelect;
  const getOptionValue = (value?: T|null) => _getOptionValue ? _getOptionValue(value) : value;
  const { items, onInputValueChange, loading, handleScroll, itemToString: _itemToString } = useSelect({
    emptyItem, pageSize, dataSelector, searchQuerySelector, itemToString, disabled: props.disabled,
    ...(asyncFunc
      ? { asyncFunc, extraParams }
      : options
        ? { options, searchFunc }
        : { options: [] as T[] })
  });
  const _onSelectedItemChange = (changes: UseComboboxStateChange<T>) => {
    if (changes?.selectedItem) {
      if (selectType === "single")
        onSelectedItemChange && onSelectedItemChange(changes.selectedItem);
      else if (selectType === "multiple") {
        let _selectedItems = [...(selectedItems || [])];
        const isSelected = _selectedItems.some((el) =>
          isEqual(getOptionValue(el), getOptionValue(changes.selectedItem as T))
        );
        if (isSelected)
          _selectedItems = _selectedItems.filter(
            (el) => !isEqual(getOptionValue(el), getOptionValue(changes.selectedItem as T))
          );
        else _selectedItems.push(changes.selectedItem);
        onSelectedListChange &&
          onSelectedListChange(_selectedItems, changes.selectedItem);
        // Fire only for Enter (not mouse ItemClick) on the opt-in picker, so the
        // consumer can advance focus once. The reducer closes the menu below.
        if (
          closeOnEnter &&
          changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter
        )
          onEnterSelect?.(changes.selectedItem);
      }
    }
  };
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    getToggleButtonProps
  } = useCombobox<T>({
    defaultIsOpen,
    onInputValueChange,
    items,
    itemToString: _itemToString,
    onSelectedItemChange: _onSelectedItemChange,
    selectedItem: selectType === "multiple" ? null : _selectedItem || null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          // Opt-in picker: Enter on a HIGHLIGHTED option closes the menu
          // (Downshift's default) so the consumer can move focus on. Enter with
          // nothing highlighted, and every non-opt-in multi-select, keep the
          // menu open exactly as before. Clear inputValue so reopening the
          // picker shows a fresh search box (matches multi-select's blur reset).
          if (closeOnEnter && state.highlightedIndex >= 0)
            return { ...changes, inputValue: "" };
          return {
            ...changes,
            ...(selectType === "multiple" && { isOpen: true })
          };
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            ...(selectType === "multiple" && { isOpen: true })
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue:
              _itemToString(changes.selectedItem as T) || changes.inputValue,
            ...(selectType === "multiple" && { inputValue: "" })
          };
        default:
          return changes;
      }
    }
  });
  return (
    <div className={cx(["relative w-full", className])}>
      <SelectInput
        selectedItems={selectedItems}
        selectType={selectType}
        onSelectedItemChange={_onSelectedItemChange}
        getInputProps={getInputProps}
        getToggleButtonProps={getToggleButtonProps}
        inputClassName={inputClassName}
        loading={items.length ? loading : false}
        isOpen={isOpen}
        getOptionLabel={getOptionLabel}
        enableKeyboardNav={keyboardNav}
        {...props}
      />
      <List
        highlightedIndex={highlightedIndex}
        getInputProps={getInputProps}
        selectType={selectType}
        items={items}
        isOpen={isOpen}
        handleScroll={handleScroll}
        getMenuProps={getMenuProps}
        getItemProps={getItemProps}
        selectedItem={selectedItem}
        selectedItems={selectedItems}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        maxHeightList={maxHeightList}
        isAbsolute={isAbsolute}
        enableKeyboardNav={keyboardNav}
      />
    </div>
  );
};
export default Select;
